import { apiClient } from "../apiClient";

export interface CouponListParams {
  page?: number;
  search?: string;
  sort?: string;
  filter?: string;
  size?: number;
}

export type CouponType =
  | "ONE_TIME"
  | "RECURRENT"
  | "DYNAMIC"
  | "WALLET_CREDIT"
  | "CITY_ACCESS"
  | "PERCENTAGE_DISCOUNT";

export interface Coupon {
  id: number;
  code: string;
  description: string;
  cityId?: number;
  applicableCityIds?: number[];
  validFrom?: string;
  expiredAT?: string;
  durationOfFreeRide?: number;
  walletCreditAmount?: number;
  discountIncludesUnlock?: boolean;
  capAtCampaignEnd?: boolean;
  honorAtRideStart?: boolean;
  discountBasisPoints?: number | null;
  validityDays?: number | null;
  rideLimit?: number | null;
  ridePass?: boolean;
  amount: number;
  validity?: number;
  numberOfOccurrences: number;
  status: string;
  forSingleUser: boolean;
  type?: CouponType;
  rewardApplyCount?: number;
  rewardPeriod?: number;
  freeRide?: boolean;
  whiteLabelId?: number | null;
  autoGenerate?: boolean;
  numberOfCoupon?: number | null;
  prefix?: string | null;
}

export interface CouponCreate {
  code?: string;
  description: string;
  cityId?: number;
  applicableCityIds?: number[];
  validFrom?: string;
  expiredAT: string;
  durationOfFreeRide?: number;
  walletCreditAmount?: number;
  discountIncludesUnlock?: boolean;
  capAtCampaignEnd?: boolean;
  honorAtRideStart?: boolean;
  discountBasisPoints?: number | null;
  validityDays?: number | null;
  rideLimit?: number | null;
  ridePass?: boolean;
  amount?: number;
  validity?: number;
  numberOfOccurrences?: number;
  forSingleUser: boolean;
  type?: CouponType;
  rewardApplyCount: number;
  rewardPeriod: number;
  freeRide: boolean;
  whiteLabelId?: number | null;
  autoGenerate?: boolean;
  numberOfCoupon?: number;
  prefix?: string;
}

export interface CouponUpdate extends Partial<CouponCreate> {}

export interface CouponListResponse {
  content: Coupon[];
  data?: Coupon[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

function buildCouponListQuery(params: CouponListParams): string {
  const queryParts: string[] = [];

  if (params.page !== undefined) {
    queryParts.push(`page=${params.page}`);
  }

  if (params.size !== undefined) {
    queryParts.push(`size=${params.size}`);
  }

  if (params.search) {
    queryParts.push(`search=${encodeURIComponent(params.search)}`);
  }

  if (params.sort) {
    queryParts.push(`sort=${params.sort}`);
  }

  if (params.filter) {
    queryParts.push(params.filter);
  }

  return queryParts.length > 0 ? `?${queryParts.join("&")}` : "";
}

export async function getCoupons(
  params: CouponListParams = {}
): Promise<CouponListResponse> {
  const query = buildCouponListQuery(params);
  const response = await apiClient.get<CouponListResponse>(
    `/coupon-service/dashboard/coupon${query}`
  );
  return response.data;
}

export async function getCouponById(id: number): Promise<Coupon> {
  const response = await apiClient.get<Coupon>(
    `/coupon-service/dashboard/coupon/${id}`
  );
  return response.data;
}

export async function createCoupon(dto: CouponCreate): Promise<Coupon> {
  const response = await apiClient.post<Coupon>(
    "/coupon-service/dashboard/coupon",
    dto
  );
  return response.data;
}

export async function updateCoupon(
  id: number,
  dto: CouponUpdate
): Promise<Coupon> {
  const response = await apiClient.put<Coupon>(
    `/coupon-service/dashboard/coupon/${id}`,
    dto
  );
  return response.data;
}

export async function deleteCoupon(id: number): Promise<void> {
  await apiClient.delete(`/coupon-service/dashboard/coupon/${id}`);
}

export async function downloadCouponsWithPrefix(
  prefix: string
): Promise<unknown> {
  const response = await apiClient.get<unknown>(
    "/coupon-service/dashboard/coupon/download/list",
    {
      params: { prefix },
      responseType: "json",
    }
  );
  return response.data;
}
